// src/pages/Platform/MarketingContent.js
//
// The Super Admin's editor for posflect.com's own marketing copy — a fixed
// set of section/key fields (hero, about, features 1-6, contact — see
// seed_marketing_content.go), never a flexible page builder, per this
// feature's own plan doc's confirmed decision. "business_types" and "faq"
// are the two exceptions: an admin can add or remove entries there (see the
// "Add a business type"/"Add FAQ" mini-forms rendered into those sections
// below), not just translate the seeded ones — see
// marketing_content_handler.go's own doc comment on
// CreateBusinessTypeHandler/DeleteBusinessTypeHandler and
// CreateFaqHandler/DeleteFaqHandler for why. One language is edited at a
// time (English itself is fixed in code — it's shown read-only as reference
// next to every field, never editable here); switching the language tab
// loads that language's own saved overrides, so translating a business type
// or FAQ (new or seeded) works exactly like translating any other field.
// Export/import mirrors Translations.js's own bespoke language-scoped modal
// conceptually, but built with Companies.js's plain-markup style (see that
// file's own doc comment on why the Platform tree can't reuse tenant-only
// components like SelectBox/Button).
import { useState, useEffect, useCallback, useContext, useRef } from "react";
import {
  getMarketingContent,
  saveMarketingContent,
  exportMarketingContent,
  importMarketingContent,
  getMarketingContact,
  updateMarketingContact,
  createBusinessType,
  deleteBusinessType,
  createFaq,
  deleteFaq,
} from "../../services/api";
import { PlatformAuthContext } from "../../context/PlatformAuthContext";

const SECTION_LABELS = {
  seo: "SEO (meta title & description)",
  hero: "Hero",
  about: "About",
  features: "Features",
  faq: "FAQ",
  contact: "Contact",
  business_types: "Demo form business types",
};

// SECTION_ORDER is a purely cosmetic display order — the backend already
// returns keys grouped by section, this just controls which section
// renders first, since Go maps/SQL "GROUP BY"-adjacent ordering gives no
// guarantee the seven sections it seeds always come back in the exact
// order a Super Admin would expect to edit them in. "seo" is listed first
// deliberately — it's the one section that isn't visible copy on the page
// itself, so it's easy to forget it's here at all if it's buried below
// Hero/About/Features.
const SECTION_ORDER = ["seo", "hero", "about", "features", "faq", "contact", "business_types"];

function groupBySection(keys) {
  const groups = {};
  for (const k of keys) {
    if (!groups[k.section]) groups[k.section] = [];
    groups[k.section].push(k);
  }
  return groups;
}

export default function MarketingContent() {
  const { token } = useContext(PlatformAuthContext);

  const [languages, setLanguages] = useState([]);
  const [keys, setKeys] = useState([]);
  const [activeLangCode, setActiveLangCode] = useState(null);
  const [draft, setDraft] = useState({}); // "section key" -> text (current language only)
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [saved, setSaved] = useState(false);

  const [transferMode, setTransferMode] = useState(null); // null | "export" | "import"
  const [transferLangCode, setTransferLangCode] = useState(null);
  const [importFile, setImportFile] = useState(null);
  const [transferBusy, setTransferBusy] = useState(false);
  const fileInputRef = useRef(null);

  // Contact phone/WhatsApp — a small panel of its own, deliberately separate
  // from the section/key/language state above: these two fields aren't
  // translated (a phone number is the same in every language), so they're
  // not part of "keys"/"draft" at all — they're PlatformSetting-backed (see
  // api.js's own doc comment on getMarketingContact/updateMarketingContact),
  // saved with their own button independent of which language tab is active.
  const [contactLoading, setContactLoading] = useState(true);
  const [contactPhone, setContactPhone] = useState("");
  const [contactWhatsapp, setContactWhatsapp] = useState("");
  // Independent "show on the public site" toggles — a Super Admin can hide
  // the phone item/WhatsApp button+float button without clearing the number
  // itself (see marketing_content_handler.go's own doc comment on
  // GetMarketingContactHandler/UpdateMarketingContactHandler). Both default
  // to true so a fresh load never shows an unchecked box for a number that's
  // actually live on the public site.
  const [contactPhoneEnabled, setContactPhoneEnabled] = useState(true);
  const [contactWhatsappEnabled, setContactWhatsappEnabled] = useState(true);
  const [contactSaving, setContactSaving] = useState(false);
  const [contactError, setContactError] = useState("");
  const [contactSaved, setContactSaved] = useState(false);

  useEffect(() => {
    (async () => {
      setContactLoading(true);
      try {
        const res = await getMarketingContact(token);
        setContactPhone(res.phone || "");
        setContactWhatsapp(res.whatsapp || "");
        setContactPhoneEnabled(res.phone_enabled !== false);
        setContactWhatsappEnabled(res.whatsapp_enabled !== false);
      } catch (err) {
        setContactError(err.message || "Failed to load contact numbers");
      } finally {
        setContactLoading(false);
      }
    })();
  }, [token]);

  const handleSaveContact = async () => {
    setContactSaving(true);
    setContactError("");
    setContactSaved(false);
    try {
      const res = await updateMarketingContact(token, {
        phone: contactPhone,
        whatsapp: contactWhatsapp,
        phone_enabled: contactPhoneEnabled,
        whatsapp_enabled: contactWhatsappEnabled,
      });
      setContactPhone(res.phone || "");
      setContactWhatsapp(res.whatsapp || "");
      setContactPhoneEnabled(res.phone_enabled !== false);
      setContactWhatsappEnabled(res.whatsapp_enabled !== false);
      setContactSaved(true);
    } catch (err) {
      setContactError(err.data?.error || err.message || "Failed to save contact numbers");
    } finally {
      setContactSaving(false);
    }
  };

  // Business types — "business_types" is the one section where an admin can
  // add/remove keys, not just translate them (see CreateBusinessTypeHandler/
  // DeleteBusinessTypeHandler, marketing_content_handler.go). A new type's
  // English label is all that's asked for here; its translations are then
  // edited exactly like any other field once it shows up in the section
  // below, via the language tabs + Save content button.
  const [newBusinessTypeLabel, setNewBusinessTypeLabel] = useState("");
  const [businessTypeBusy, setBusinessTypeBusy] = useState(false);
  const [businessTypeError, setBusinessTypeError] = useState("");

  // FAQ — the other section where an admin can add/remove keys, not just
  // translate them (see CreateFaqHandler/DeleteFaqHandler,
  // marketing_content_handler.go). Unlike a business type, one FAQ entry is
  // a PAIR of keys ("<id>_question"/"<id>_answer"), so the English question
  // and answer are both asked for up front here, and both then get
  // translated together — one row per key, exactly like every other field,
  // once the pair shows up in the section below.
  const [newFaqQuestion, setNewFaqQuestion] = useState("");
  const [newFaqAnswer, setNewFaqAnswer] = useState("");
  const [faqBusy, setFaqBusy] = useState(false);
  const [faqError, setFaqError] = useState("");

  const buildDraft = (keyList, langCode) => {
    const next = {};
    for (const k of keyList) {
      next[k.section + " " + k.key] = (langCode && k.values?.[langCode]) || "";
    }
    return next;
  };

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await getMarketingContent(token);
      const langs = res.languages || [];
      setLanguages(langs);
      setKeys(res.keys || []);
      const firstLang = activeLangCode && langs.some((l) => l.code === activeLangCode)
        ? activeLangCode
        : langs[0]?.code || null;
      setActiveLangCode(firstLang);
      setDraft(buildDraft(res.keys || [], firstLang));
    } catch (err) {
      setError(err.message || "Failed to load marketing content");
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const switchLang = (code) => {
    setActiveLangCode(code);
    setDraft(buildDraft(keys, code));
    setSaved(false);
  };

  const handleSave = async () => {
    if (!activeLangCode) return;
    setSaving(true);
    setError("");
    setSaved(false);
    try {
      const items = Object.entries(draft).map(([compound, text]) => {
        const [section, key] = compound.split(" ");
        return { section, key, text };
      });
      await saveMarketingContent(token, activeLangCode, items);
      setSaved(true);
      await load();
    } catch (err) {
      setError(err.message || "Failed to save marketing content");
    } finally {
      setSaving(false);
    }
  };

  const handleAddBusinessType = async () => {
    const label = newBusinessTypeLabel.trim();
    if (!label) return;
    setBusinessTypeBusy(true);
    setBusinessTypeError("");
    try {
      await createBusinessType(token, label);
      setNewBusinessTypeLabel("");
      await load();
    } catch (err) {
      setBusinessTypeError(err.data?.error || err.message || "Failed to add business type");
    } finally {
      setBusinessTypeBusy(false);
    }
  };

  const handleDeleteBusinessType = async (k) => {
    if (!window.confirm(`Remove "${k.english_text || k.key}"? This can't be undone.`)) return;
    setBusinessTypeBusy(true);
    setBusinessTypeError("");
    try {
      await deleteBusinessType(token, k.key);
      await load();
    } catch (err) {
      setBusinessTypeError(err.data?.error || err.message || "Failed to remove business type");
    } finally {
      setBusinessTypeBusy(false);
    }
  };

  const handleAddFaq = async () => {
    const question = newFaqQuestion.trim();
    const answer = newFaqAnswer.trim();
    if (!question || !answer) return;
    setFaqBusy(true);
    setFaqError("");
    try {
      await createFaq(token, question, answer);
      setNewFaqQuestion("");
      setNewFaqAnswer("");
      await load();
    } catch (err) {
      setFaqError(err.data?.error || err.message || "Failed to add FAQ");
    } finally {
      setFaqBusy(false);
    }
  };

  // k is the "<id>_question" row (the only one Remove is rendered on below)
  // — the id itself is that key with "_question" stripped, and
  // DeleteFaqHandler takes it from there to find and remove both halves of
  // the pair.
  const handleDeleteFaq = async (k) => {
    if (!window.confirm(`Remove this FAQ ("${k.english_text || k.key}")? This can't be undone.`)) return;
    const id = k.key.replace(/_question$/, "");
    setFaqBusy(true);
    setFaqError("");
    try {
      await deleteFaq(token, id);
      await load();
    } catch (err) {
      setFaqError(err.data?.error || err.message || "Failed to remove FAQ");
    } finally {
      setFaqBusy(false);
    }
  };

  const closeTransferModal = () => {
    if (transferBusy) return;
    setTransferMode(null);
    setTransferLangCode(null);
    setImportFile(null);
  };

  const handleExport = async () => {
    if (!transferLangCode) return;
    setTransferBusy(true);
    try {
      const blob = await exportMarketingContent(token, transferLangCode);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `marketing_content_${transferLangCode}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      closeTransferModal();
    } catch (err) {
      alert(err.data?.error || err.message || "Failed to export marketing content");
    } finally {
      setTransferBusy(false);
    }
  };

  const handleImport = async () => {
    if (!transferLangCode || !importFile) return;
    setTransferBusy(true);
    try {
      const result = await importMarketingContent(token, transferLangCode, importFile);
      alert(`Imported ${result.imported}, cleared ${result.deleted}, skipped ${result.skipped}`);
      closeTransferModal();
      await load();
    } catch (err) {
      alert(err.data?.error || err.message || "Failed to import marketing content");
    } finally {
      setTransferBusy(false);
    }
  };

  if (loading) return <div style={{ padding: 40, textAlign: "center", color: "#94a3b8" }}>Loading...</div>;

  const grouped = groupBySection(keys);

  return (
    <div style={{ maxWidth: 900 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20, flexWrap: "wrap", gap: 10 }}>
        <h2 style={{ margin: 0, fontSize: 20, color: "#0f172a" }}>Marketing site content</h2>
        <div style={{ display: "flex", gap: 8 }}>
          <button style={secondaryBtnStyle} onClick={() => setTransferMode("export")}>Export</button>
          <button style={secondaryBtnStyle} onClick={() => setTransferMode("import")}>Import</button>
        </div>
      </div>

      <section style={sectionStyle}>
        <h3 style={sectionTitleStyle}>Contact numbers</h3>
        <p style={{ fontSize: 13, color: "#64748b", marginTop: -6, marginBottom: 14 }}>
          Shown on posflect.com's Contact section, and used for the Call/WhatsApp buttons — the same for every
          language, so it's separate from the language tabs below. Include the country code (e.g. +971 50 690 2625).
          Each checkbox below hides that button on the public site without clearing the number.
        </p>
        {contactLoading ? (
          <div style={{ color: "#94a3b8", fontSize: 13 }}>Loading...</div>
        ) : (
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
            <div>
              <div style={fieldLabelStyle}>Phone</div>
              <input
                style={inputStyle}
                value={contactPhone}
                onChange={(e) => setContactPhone(e.target.value)}
                placeholder="+971 50 690 2625"
              />
              <label style={contactToggleLabelStyle}>
                <input
                  type="checkbox"
                  checked={contactPhoneEnabled}
                  onChange={(e) => setContactPhoneEnabled(e.target.checked)}
                />
                Show on the public site (call button + number)
              </label>
            </div>
            <div>
              <div style={fieldLabelStyle}>WhatsApp</div>
              <input
                style={inputStyle}
                value={contactWhatsapp}
                onChange={(e) => setContactWhatsapp(e.target.value)}
                placeholder="+971 50 690 2625"
              />
              <label style={contactToggleLabelStyle}>
                <input
                  type="checkbox"
                  checked={contactWhatsappEnabled}
                  onChange={(e) => setContactWhatsappEnabled(e.target.checked)}
                />
                Show on the public site (WhatsApp button + float)
              </label>
            </div>
          </div>
        )}
        {contactError && <div style={{ ...errorBoxStyle, marginTop: 14 }}>{contactError}</div>}
        {contactSaved && <div style={{ ...successBoxStyle, marginTop: 14 }}>Contact numbers saved.</div>}
        <button
          type="button"
          style={{ ...primaryBtnStyle, marginTop: 14 }}
          onClick={handleSaveContact}
          disabled={contactSaving || contactLoading}
        >
          {contactSaving ? "Saving..." : "Save contact numbers"}
        </button>
      </section>

      {languages.length === 0 ? (
        <div style={{ padding: 24, color: "#94a3b8", textAlign: "center", background: "#fff", borderRadius: 12, border: "1px solid #e2e8f0" }}>
          No additional languages yet — add one from the Languages page first. (English is always the built-in default.)
        </div>
      ) : (
        <>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 20 }}>
            {languages.map((l) => (
              <button
                key={l.code}
                onClick={() => switchLang(l.code)}
                style={{
                  ...secondaryBtnStyle,
                  background: activeLangCode === l.code ? "#0f172a" : "#fff",
                  color: activeLangCode === l.code ? "#fff" : "#334155",
                }}
              >
                {l.name}
              </button>
            ))}
          </div>

          {SECTION_ORDER.filter((s) => grouped[s]?.length || s === "business_types" || s === "faq").map((section) => (
            <section key={section} style={sectionStyle}>
              <h3 style={sectionTitleStyle}>{SECTION_LABELS[section] || section}</h3>
              {section === "seo" && (
                <p style={{ fontSize: 13, color: "#64748b", marginTop: -6, marginBottom: 14 }}>
                  What search engines and shared links show for posflect.com — the browser tab title, the
                  search-result snippet, and social-preview cards on WhatsApp/Facebook/Twitter/LinkedIn. Keep
                  the title under ~60 characters and the description under ~160 — most search engines cut off
                  anything longer.
                </p>
              )}
              {section === "business_types" && (
                <p style={{ fontSize: 13, color: "#64748b", marginTop: -6, marginBottom: 14 }}>
                  Add or remove activity types the demo form's dropdown offers — then translate each one the
                  same way as any other field below, by switching language tabs.
                </p>
              )}
              {section === "faq" && (
                <p style={{ fontSize: 13, color: "#64748b", marginTop: -6, marginBottom: 14 }}>
                  Add or remove questions shown in the public site's FAQ accordion — then translate each one
                  the same way as any other field below, by switching language tabs.
                </p>
              )}
              {(grouped[section] || []).map((k) => {
                const showRemove = section === "business_types" || (section === "faq" && k.key.endsWith("_question"));
                return (
                <div key={k.key} style={{ display: "grid", gridTemplateColumns: showRemove ? "1fr 1fr auto" : "1fr 1fr", gap: 16, marginBottom: 14, alignItems: "end" }}>
                  <div>
                    <div style={fieldLabelStyle}>{k.key.replace(/_/g, " ")}</div>
                    <div style={referenceBoxStyle}>{k.english_text || <em style={{ color: "#cbd5e1" }}>(empty)</em>}</div>
                  </div>
                  <div>
                    <div style={fieldLabelStyle}>&nbsp;</div>
                    {k.key === "body" || k.key === "description" || k.key.endsWith("_description") || k.key.endsWith("_answer") ? (
                      <textarea
                        style={{ ...inputStyle, minHeight: 70, resize: "vertical" }}
                        value={draft[k.section + " " + k.key] ?? ""}
                        onChange={(e) => setDraft((d) => ({ ...d, [k.section + " " + k.key]: e.target.value }))}
                        placeholder={k.english_text}
                      />
                    ) : (
                      <input
                        style={inputStyle}
                        value={draft[k.section + " " + k.key] ?? ""}
                        onChange={(e) => setDraft((d) => ({ ...d, [k.section + " " + k.key]: e.target.value }))}
                        placeholder={k.english_text}
                      />
                    )}
                  </div>
                  {showRemove && (
                    <button
                      type="button"
                      title={section === "business_types" ? "Remove this business type" : "Remove this FAQ"}
                      onClick={() => (section === "business_types" ? handleDeleteBusinessType(k) : handleDeleteFaq(k))}
                      disabled={section === "business_types" ? businessTypeBusy : faqBusy}
                      style={removeBtnStyle}
                    >
                      Remove
                    </button>
                  )}
                </div>
                );
              })}
              {section === "business_types" && (
                <div style={{ display: "flex", gap: 10, marginTop: 6, alignItems: "center", flexWrap: "wrap" }}>
                  <input
                    style={{ ...inputStyle, maxWidth: 280 }}
                    value={newBusinessTypeLabel}
                    onChange={(e) => setNewBusinessTypeLabel(e.target.value)}
                    placeholder="e.g. Bakery"
                    onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); handleAddBusinessType(); } }}
                  />
                  <button
                    type="button"
                    style={secondaryBtnStyle}
                    onClick={handleAddBusinessType}
                    disabled={businessTypeBusy || !newBusinessTypeLabel.trim()}
                  >
                    {businessTypeBusy ? "Working..." : "Add business type"}
                  </button>
                </div>
              )}
              {section === "business_types" && businessTypeError && (
                <div style={{ ...errorBoxStyle, marginTop: 12 }}>{businessTypeError}</div>
              )}
              {section === "faq" && (
                <div style={{ display: "flex", flexDirection: "column", gap: 10, marginTop: 6 }}>
                  <input
                    style={inputStyle}
                    value={newFaqQuestion}
                    onChange={(e) => setNewFaqQuestion(e.target.value)}
                    placeholder="New question"
                  />
                  <textarea
                    style={{ ...inputStyle, minHeight: 60, resize: "vertical" }}
                    value={newFaqAnswer}
                    onChange={(e) => setNewFaqAnswer(e.target.value)}
                    placeholder="New answer"
                  />
                  <button
                    type="button"
                    style={{ ...secondaryBtnStyle, alignSelf: "flex-start" }}
                    onClick={handleAddFaq}
                    disabled={faqBusy || !newFaqQuestion.trim() || !newFaqAnswer.trim()}
                  >
                    {faqBusy ? "Working..." : "Add FAQ"}
                  </button>
                </div>
              )}
              {section === "faq" && faqError && (
                <div style={{ ...errorBoxStyle, marginTop: 12 }}>{faqError}</div>
              )}
            </section>
          ))}

          {error && <div style={errorBoxStyle}>{error}</div>}
          {saved && <div style={successBoxStyle}>Marketing content saved.</div>}

          <button type="button" style={primaryBtnStyle} onClick={handleSave} disabled={saving || !activeLangCode}>
            {saving ? "Saving..." : "Save content"}
          </button>
        </>
      )}

      {transferMode && (
        <div style={overlayStyle}>
          <div style={{ ...modalStyle, width: 420 }}>
            <h3 style={{ marginTop: 0 }}>
              {transferMode === "export" ? "Export marketing content" : "Import marketing content"}
            </h3>
            <p style={{ fontSize: 13, color: "#64748b" }}>
              {transferMode === "export"
                ? "Downloads one CSV covering every section/key for the language you pick — filled in wherever a translation already exists, blank everywhere else."
                : "Pick the language the file is for, then upload a CSV built by Export (or matching its section/key/translation columns)."}
            </p>

            {languages.length === 0 ? (
              <div style={{ padding: 16, color: "#94a3b8", textAlign: "center" }}>
                No additional languages yet — add one from the Languages page first.
              </div>
            ) : (
              <>
                <label style={labelStyle}>Language</label>
                <select
                  style={inputStyle}
                  value={transferLangCode || ""}
                  onChange={(e) => setTransferLangCode(e.target.value || null)}
                >
                  <option value="">Choose a language…</option>
                  {languages.map((l) => (
                    <option key={l.code} value={l.code}>{l.name}</option>
                  ))}
                </select>

                {transferMode === "import" && (
                  <div style={{ marginTop: 12 }}>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept=".csv"
                      style={{ display: "none" }}
                      onChange={(e) => setImportFile(e.target.files?.[0] || null)}
                    />
                    <button
                      type="button"
                      style={{ ...secondaryBtnStyle, width: "100%", textAlign: "left" }}
                      onClick={() => fileInputRef.current?.click()}
                    >
                      {importFile ? importFile.name : "Choose a CSV file…"}
                    </button>
                  </div>
                )}
              </>
            )}

            <div style={{ display: "flex", gap: 8, marginTop: 20 }}>
              <button type="button" style={secondaryBtnStyle} onClick={closeTransferModal} disabled={transferBusy}>
                Cancel
              </button>
              {languages.length > 0 && (
                <button
                  type="button"
                  style={primaryBtnStyle}
                  onClick={transferMode === "export" ? handleExport : handleImport}
                  disabled={transferBusy || !transferLangCode || (transferMode === "import" && !importFile)}
                >
                  {transferBusy ? "Working..." : transferMode === "export" ? "Export" : "Import"}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const sectionStyle = { background: "#fff", border: "1px solid #e2e8f0", borderRadius: 12, padding: 18, marginBottom: 16 };
const sectionTitleStyle = { margin: "0 0 14px", fontSize: 14, color: "#0f172a" };
const fieldLabelStyle = { fontSize: 11, fontWeight: 600, color: "#94a3b8", textTransform: "uppercase", marginBottom: 4 };
const contactToggleLabelStyle = { display: "flex", alignItems: "center", gap: 6, marginTop: 8, fontSize: 13, color: "#334155", cursor: "pointer" };
const referenceBoxStyle = { fontSize: 13, color: "#475569", background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 8, padding: "8px 10px", minHeight: 36 };
const inputStyle = { padding: "9px 12px", borderRadius: 8, border: "1px solid #cbd5e1", fontSize: 14, width: "100%", boxSizing: "border-box" };
const labelStyle = { display: "block", fontSize: 12, fontWeight: 600, color: "#475569", margin: "12px 0 4px" };
const primaryBtnStyle = { background: "#0f172a", color: "#fff", border: "none", borderRadius: 8, padding: "10px 18px", fontSize: 14, fontWeight: 600, cursor: "pointer" };
const secondaryBtnStyle = { background: "#fff", color: "#334155", border: "1px solid #cbd5e1", borderRadius: 8, padding: "9px 14px", fontSize: 14, cursor: "pointer" };
const removeBtnStyle = { background: "#fff", color: "#b91c1c", border: "1px solid #fecaca", borderRadius: 8, padding: "9px 12px", fontSize: 13, cursor: "pointer", whiteSpace: "nowrap", height: "fit-content" };
const errorBoxStyle = { background: "#fee2e2", color: "#991b1b", borderRadius: 8, padding: "8px 12px", fontSize: 13, marginBottom: 12 };
const successBoxStyle = { background: "#dcfce7", color: "#166534", borderRadius: 8, padding: "8px 12px", fontSize: 13, marginBottom: 12 };
const overlayStyle = { position: "fixed", inset: 0, background: "rgba(15, 23, 42, 0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 };
const modalStyle = { background: "#fff", borderRadius: 12, padding: 24, width: 420, maxWidth: "90vw", maxHeight: "90vh", overflowY: "auto" };
